/**
 * The access page: category, then organisation, then role, then credentials.
 *
 * Three steps rather than one email box, because on this platform who you are
 * acting as matters as much as who you are. The same person may hold a role at
 * a bank and a role at the guarantee agency, and a session is opened in one
 * role, not in both: the audit trail says which, and a role switch ends one
 * session and opens another.
 *
 * Seeded accounts and a shared password appear only while the deployment runs
 * in demonstration mode, and the server decides that, not this screen. The
 * front end cannot be built in a way that reveals them in production, because
 * it never has them.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api, ApiError, IS_STATIC } from '../api/client'
import type { AccessPayload } from '../api/types'
import { absolutePath } from '../app/paths'
import { Badge, Note, Spinner } from '../components/bits'
import { Diagnosis } from '../components/Diagnosis'
import { StaticAccess } from './StaticAccess'

export function Access() {
  // The static build has no credential to take and no platform to take it to,
  // so it chooses a role out of the recording instead. Branching once here
  // rather than inside the form keeps the live sign-in exactly as it was.
  if (IS_STATIC) return <StaticAccess />
  return <LiveAccess />
}

function LiveAccess() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['access'], queryFn: api.access, retry: 1,
  })

  if (isLoading) return <div className="signin"><Spinner what="the access page" /></div>
  if (error || !data) {
    return (
      <div className="signin">
        <header className="signin-head">
          <span className="plate lg">FP</span>
          <div>
            <h1>Funding Plate</h1>
            <p>The platform did not answer, so there is nothing to sign in to
               yet.</p>
          </div>
        </header>
        <Diagnosis problem={(error as Error)?.message} />
      </div>
    )
  }
  return <AccessForm access={data} />
}

function AccessForm({ access }: { access: AccessPayload }) {
  const [category, setCategory] = useState<string | null>(null)
  const [partner, setPartner] = useState<string | null>(null)
  const [role, setRole] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState(access.demo_password || '')
  const [busy, setBusy] = useState(false)
  const [refused, setRefused] = useState<string | null>(null)

  const isCustomer = category === 'CUSTOMER'

  const partners = useMemo(
    () => access.partners.filter((p) => p.category_code === category),
    [access.partners, category])

  const roles = useMemo(() => {
    if (isCustomer) return access.customer_roles
    return access.partner_roles.filter((r) => r.partner_code === partner)
  }, [access.customer_roles, access.partner_roles, isCustomer, partner])

  const accounts = useMemo(() => {
    if (!role) return []
    return access.demo_accounts.filter(
      (a) => a.role_code === role && (isCustomer || a.partner_code === partner))
  }, [access.demo_accounts, role, partner, isCustomer])

  const signIn = async () => {
    if (!role) return
    setBusy(true)
    setRefused(null)
    try {
      const result = await api.signIn({
        email, password, role_code: role,
        ...(partner && !isCustomer ? { partner_code: partner } : {}),
      })
      window.location.assign(absolutePath(result.landing))
    } catch (e) {
      const problem = e as ApiError
      setRefused(problem.detail || problem.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="signin">
      <header className="signin-head">
        <span className="plate lg">FP</span>
        <div>
          <h1>{access.app_name}</h1>
          <p>National MSME growth orchestration. Sign in as the organisation and
             the role you are acting for.</p>
          <div className="scope-note">
            <Badge tone={access.environment === 'production' ? 'bad' : 'warn'}>
              {access.environment}
            </Badge>
            <Badge tone="muted">react front end</Badge>
            {access.demo_mode && <Badge tone="warn">demonstration mode</Badge>}
          </div>
        </div>
      </header>

      {access.demo_mode && (
        <Note kind="warn">
          <b>This deployment is in demonstration mode.</b> Seeded accounts are
          listed below and one shared password is filled in. A real deployment
          runs with that mode off, where this page lists nothing and fills
          nothing.
        </Note>
      )}

      <ol className="steps">
        <li className={category ? 'done' : 'now'}>
          <h2>1 · Which kind of participant are you</h2>
          <div className="pick">
            {access.categories.map((c) => (
              <button type="button" key={c.category_code}
                      className={`pick-card ${category === c.category_code ? 'on' : ''}`}
                      onClick={() => { setCategory(c.category_code); setPartner(null); setRole(null) }}>
                <b>{c.name_en}</b>
                <span className="ar" dir="rtl">{c.name_ar}</span>
                <small>{c.description}</small>
                <span className="xs muted">
                  {c.partners} organisation{c.partners === 1 ? '' : 's'} ·{' '}
                  {c.roles} role{c.roles === 1 ? '' : 's'}
                </span>
              </button>
            ))}
          </div>
        </li>

        {category && !isCustomer && (
          <li className={partner ? 'done' : 'now'}>
            <h2>2 · Which organisation</h2>
            <div className="pick">
              {partners.map((p) => (
                <button type="button" key={p.partner_code}
                        className={`pick-card ${partner === p.partner_code ? 'on' : ''}`}
                        onClick={() => { setPartner(p.partner_code); setRole(null) }}>
                  <span className="plate sm"
                        style={{ background: p.brand_colour || undefined }}>
                    {p.logo_initials || p.partner_code.slice(0, 2)}
                  </span>
                  <b>{p.legal_name_en}</b>
                  <span className="ar" dir="rtl">{p.legal_name_ar}</span>
                  <span className="xs muted">
                    {p.roles_licensed} role{p.roles_licensed === 1 ? '' : 's'} licensed
                  </span>
                  {p.is_illustrative && (
                    <Badge tone="warn">illustrative · {p.release_phase}</Badge>
                  )}
                </button>
              ))}
            </div>
            <p className="xs muted">
              An organisation marked illustrative shows where a participant type
              sits in the ecosystem. It holds no users and nothing can be signed
              in against it.
            </p>
          </li>
        )}

        {category && (isCustomer || partner) && (
          <li className={role ? 'done' : 'now'}>
            <h2>{isCustomer ? '2' : '3'} · Which role are you acting in</h2>
            <div className="pick">
              {roles.map((r) => (
                <button type="button" key={r.role_code}
                        className={`pick-card ${role === r.role_code ? 'on' : ''}`}
                        onClick={() => setRole(r.role_code)}>
                  <b>{r.name_en}</b>
                  <span className="ar" dir="rtl">{r.name_ar}</span>
                  <small>{r.purpose}</small>
                  <span className="xs muted">
                    <code>{r.role_code}</code> · {r.seats} account
                    {r.seats === 1 ? '' : 's'}
                  </span>
                  {'requires_mfa' in r && Boolean(r.requires_mfa) && (
                    <Badge tone="warn">second factor required</Badge>
                  )}
                </button>
              ))}
            </div>
            {roles.length === 0 && (
              <p className="muted">
                No role here holds an account. Nothing can be signed in against
                this organisation.
              </p>
            )}
          </li>
        )}

        {role && (
          <li className="now">
            <h2>{isCustomer ? '3' : '4'} · Sign in</h2>

            {accounts.length > 0 && (
              <div className="field wide">
                <label htmlFor="who">Seeded accounts in this role</label>
                <select id="who" value={email}
                        onChange={(e) => setEmail(e.target.value)}>
                  <option value="">Choose an account</option>
                  {accounts.map((a) => (
                    <option value={a.email} key={a.email}>
                      {a.display_name}{a.job_title ? ` · ${a.job_title}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="inline-form">
              <div className="field wide">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" value={email} autoComplete="username"
                       onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <input id="password" type="password" value={password}
                       autoComplete="current-password"
                       onChange={(e) => setPassword(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') void signIn() }} />
              </div>
              <button className="btn primary" type="button"
                      disabled={busy || !email || !password}
                      onClick={() => void signIn()}>
                {busy ? 'Signing in…' : `Sign in as ${role}`}
              </button>
            </div>

            {refused && (
              <Note kind="bad"><b>Sign in refused.</b> {refused}</Note>
            )}

            <p className="xs muted">
              A refusal says only that the attempt failed. Which of the email,
              the password or the role was wrong is recorded in the attempt log
              and is not told to whoever is at the keyboard.
            </p>
          </li>
        )}
      </ol>

      <footer className="signin-foot">
        <p className="xs muted">
          This is the React front end. The server rendered tier is at{' '}
          <a href="/">/</a> and both run against the same database, the same
          permission model and the same engines.
        </p>
      </footer>
    </div>
  )
}
